import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getStrategies, deleteStrategy, ApiError } from '../api/apiClient';
import {
  HiOutlinePlusCircle,
  HiArrowUp,
  HiArrowDown,
  HiTrash,
  HiOutlineClock,
  HiOutlineArrowsExpand,
  HiOutlineChartBar,
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
  HiOutlineCurrencyDollar,
  HiOutlineScale
} from 'react-icons/hi';
import Modal from '../components/shared/Modal';

const formatDate = (value, locale) => {
  const date = new Date(value || 0);
  if (!Number.isFinite(date.getTime()) || date.getTime() <= 0) return '-';
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ParameterItem = ({ icon, label, value }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-700/60 bg-slate-900/60 px-3 py-2.5">
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-slate-400">{icon}</span>
      <span className="text-xs text-slate-400 truncate">{label}</span>
    </div>
    <span
      className="font-mono text-xs font-semibold text-slate-100 bg-slate-700/60 px-2 py-1 rounded-lg truncate"
      title={String(value)}
    >
      {value}
    </span>
  </div>
);

const StrategyCard = ({ strategy, onDelete, locale }) => {
  const { t } = useTranslation();
  const { customStrategyName, strategyName, parameters, updatedAt, createdAt, _id, strategyId, publicStrategyId } = strategy;

  const linkId = publicStrategyId || strategyId || _id;
  const displayName = customStrategyName || strategyName || t('hub.untitled');
  const lastEdited = formatDate(updatedAt || createdAt || Date.now(), locale);

  if (!parameters) {
    return (
      <div className="h-full rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-center flex flex-col justify-center">
        <h3 className="text-lg font-bold text-white mb-2">{displayName}</h3>
        <p className="text-sm text-rose-300">{t('hub.corrupted_data')}</p>
      </div>
    );
  }

  const pairValue = [parameters.symbol_a, parameters.symbol_b].filter(Boolean).join(' / ') || '-';
  const timeframeValue = parameters.timeframe ? `${parameters.timeframe}m` : '-';
  const leverageValue = `${parameters.leverage || 1}x`;
  const marginValue = `$${parameters.margin || 0}`;
  const zScoreValue = parameters.zscore_period ?? '-';
  const positiveLevels = parameters.level_entry_positive?.join(', ') || t('hub.none');
  const negativeLevels = parameters.level_entry_negative?.join(', ') || t('hub.none');
  const description = parameters.description || strategy.description || '';

  return (
    <div className="group relative h-full">
      {linkId ? (
        <Link
          to={`/strategies/${linkId}`}
          className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-700/70 bg-gradient-to-br from-slate-800/80 via-slate-800/70 to-blue-950/35 p-5 shadow-xl shadow-slate-950/25 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/60 hover:shadow-blue-950/35"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(37,99,235,0.14),_transparent_55%)]" />
          <div className="relative z-10 flex h-full flex-col">
            <div className="pr-10">
              <h3 className="text-lg font-bold text-white break-words leading-snug">{displayName}</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-1 text-[11px] font-semibold text-blue-200">
                  <HiOutlineArrowsExpand className="w-3.5 h-3.5" />
                  {pairValue}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-600/70 bg-slate-900/65 px-2.5 py-1 text-[11px] font-semibold text-slate-200">
                  <HiOutlineClock className="w-3.5 h-3.5" />
                  {timeframeValue}
                </span>
              </div>
            </div>

            {description ? (
              <p className="text-sm text-slate-400 mt-3 mb-4 line-clamp-2 min-h-[40px]" title={description}>
                {description}
              </p>
            ) : (
              <div className="min-h-[40px] mt-3 mb-4" />
            )}

            <div className="grid grid-cols-1 gap-2.5 flex-1">
              <ParameterItem
                icon={<HiOutlineScale className="w-4 h-4 text-amber-300" />}
                label={t('strategy_settings.leverage')}
                value={leverageValue}
              />
              <ParameterItem
                icon={<HiOutlineCurrencyDollar className="w-4 h-4 text-blue-300" />}
                label={t('strategy_settings.margin')}
                value={marginValue}
              />
              <ParameterItem
                icon={<HiOutlineChartBar className="w-4 h-4" />}
                label={t('hub.params.zscore')}
                value={zScoreValue}
              />
              <ParameterItem
                icon={<HiOutlineTrendingUp className="w-4 h-4 text-emerald-400" />}
                label={t('hub.params.entry_plus')}
                value={positiveLevels}
              />
              <ParameterItem
                icon={<HiOutlineTrendingDown className="w-4 h-4 text-rose-400" />}
                label={t('hub.params.entry_minus')}
                value={negativeLevels}
              />
            </div>

            <div className="mt-4 pt-3 border-t border-slate-700/60 flex items-center justify-between text-xs">
              <span className="text-slate-500">{t('hub.last_edited')}</span>
              <span className="text-slate-300 font-mono">{lastEdited}</span>
            </div>
          </div>
        </Link>
      ) : (
        <div className="h-full rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 flex items-center justify-center text-center">
          <span className="text-rose-300 text-sm">{t('hub.error_id')}</span>
        </div>
      )}

      {linkId && (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDelete({ publicId: linkId, name: displayName });
          }}
          className="absolute top-3 right-3 p-2 rounded-lg bg-slate-950/60 text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-rose-600 hover:text-white transition-all"
          title={t('hub.modal.btn_delete')}
        >
          <HiTrash className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

const CreateStrategyCard = () => {
  const { t } = useTranslation();
  return (
    <Link
      to="/strategies/new"
      className="group relative flex h-full min-h-[320px] flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed border-blue-500/35 bg-gradient-to-br from-slate-800/80 via-slate-800/70 to-blue-950/35 p-6 shadow-xl shadow-slate-950/30 transition-all duration-300 hover:-translate-y-1 hover:border-blue-400"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.2),_transparent_60%)]" />
      <div className="pointer-events-none absolute -bottom-16 right-2 h-40 w-40 rounded-full bg-blue-500/15 blur-3xl" />
      <div className="relative z-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-blue-500/30 bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
          <HiOutlinePlusCircle className="h-9 w-9 text-blue-300" />
        </div>
        <h3 className="text-xl font-bold text-white mt-5">{t('hub.create_new')}</h3>
        <p className="text-sm text-slate-400 mt-2">{t('detail.new_strategy')}</p>
      </div>
    </Link>
  );
};

const DeleteStrategyModal = ({ strategy, onClose, onConfirm, status }) => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={!!strategy} onClose={onClose} title={t('hub.modal.confirm_title')}>
      <div className="text-center p-2">
        <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-rose-500/20 mb-4">
          <HiTrash className="h-8 w-8 text-rose-500" />
        </div>
        <h3 className="text-lg font-medium text-white">{t('hub.modal.question')}</h3>
        <p className="text-slate-400 mt-2 text-sm">{t('hub.modal.message', { name: strategy?.name })}</p>
        {status.error && (
          <div className="mt-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded text-rose-400 text-sm">
            {status.error}
          </div>
        )}
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={status.loading}
            className="px-5 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium transition-colors"
          >
            {t('hub.modal.btn_cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(strategy)}
            disabled={status.loading}
            className="px-5 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium shadow-lg shadow-rose-500/20 transition-colors"
          >
            {status.loading ? t('hub.modal.deleting') : t('hub.modal.btn_delete')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const StrategiesHubPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  const [strategies, setStrategies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState({ loading: false, error: null });

  useEffect(() => {
    const loadStrategies = async () => {
      if (!user?.login) {
        setStrategies([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getStrategies(user.login);
        setStrategies(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load strategies:', error);
        setStrategies([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadStrategies();
  }, [user?.login]);

  const sortedStrategies = useMemo(() => {
    const sortableItems = [...strategies];
    sortableItems.sort((a, b) => {
      if (sortConfig.key === 'customStrategyName') {
        const nameA = a.customStrategyName || a.strategyName || '';
        const nameB = b.customStrategyName || b.strategyName || '';
        return nameA.localeCompare(nameB, locale) * (sortConfig.direction === 'asc' ? 1 : -1);
      }

      const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return (dateB - dateA) * (sortConfig.direction === 'asc' ? -1 : 1);
    });
    return sortableItems;
  }, [strategies, sortConfig, locale]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDeleteConfirm = async (target) => {
    if (!target || !target.publicId) return;
    setDeleteStatus({ loading: true, error: null });

    try {
      await deleteStrategy(target.publicId);
      setStrategies((prev) => prev.filter((item) => (item.publicStrategyId || item.strategyId || item._id) !== target.publicId));
      setStrategyToDelete(null);
      setDeleteStatus({ loading: false, error: null });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('hub.modal.error_delete');
      setDeleteStatus({ loading: false, error: message });
    }
  };

  const SortButton = ({ sortKey, children }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <button
        type="button"
        onClick={() => handleSort(sortKey)}
        className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${
          isActive
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/35'
            : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700/80'
        }`}
      >
        <span>{children}</span>
        {isActive && (sortConfig.direction === 'asc' ? <HiArrowUp className="h-3.5 w-3.5" /> : <HiArrowDown className="h-3.5 w-3.5" />)}
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 animate-fadeIn">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 items-start">
        <h1 className="text-3xl font-bold text-white">{t('hub.page_title')}</h1>
        <div className="w-full md:w-auto rounded-xl border border-slate-700/70 bg-slate-900/60 p-1.5 shadow-inner shadow-slate-950/50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{t('hub.sort_by')}</span>
            <SortButton sortKey="customStrategyName">{t('hub.sort_name')}</SortButton>
            <SortButton sortKey="updatedAt">{t('hub.sort_date')}</SortButton>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5 auto-rows-fr">
        <CreateStrategyCard />
        {sortedStrategies.map((strategy) => (
          <StrategyCard
            key={strategy.publicStrategyId || strategy.strategyId || strategy._id}
            strategy={strategy}
            onDelete={setStrategyToDelete}
            locale={locale}
          />
        ))}
      </section>

      <DeleteStrategyModal
        strategy={strategyToDelete}
        onClose={() => setStrategyToDelete(null)}
        onConfirm={handleDeleteConfirm}
        status={deleteStatus}
      />
    </div>
  );
};

export default StrategiesHubPage;
